import type { Me, User } from '../api/types'
import type { LocalChannel } from '../db/db'
import { botIdOf } from './bots'
import { describeLastMedia } from './media'

export type UserMap = Map<string, User>

export function userName(users: UserMap, id: string | null | undefined, fallback = 'Unknown user'): string {
  if (!id) return fallback
  const user = users.get(id)
  if (!user) return fallback
  // Integration actors (managing systems) and bots are inactive by design: show their name as is, e.g. "HR system".
  return user.isActive || user.kind !== 'person' ? user.displayName : `${user.displayName} (deactivated)`
}

/** Other members of a DM or group DM. */
export function otherMemberIds(channel: LocalChannel, meId: string | null): string[] {
  return channel.memberIds.filter((id) => id !== meId)
}

export function channelDisplayName(channel: LocalChannel, users: UserMap, meId: string | null): string {
  if (channel.type === 'channel') return channel.name ?? 'Untitled channel'
  if (channel.name) return channel.name
  if (channel.type === 'bot_dm') {
    // Named after the bot. An admin looking at someone else's bot conversation sees both names.
    const botId = botIdOf(channel, users, meId)
    const botName = (botId && users.get(botId)?.displayName) || 'Bot'
    if (meId && channel.memberIds.includes(meId)) return botName
    const personId = channel.memberIds.find((id) => id !== botId)
    return personId ? `${botName} and ${users.get(personId)?.displayName ?? 'Unknown user'}` : botName
  }
  const others = otherMemberIds(channel, meId)
  if (others.length === 0) return meId ? `${userName(users, meId, 'You')} (you)` : 'Direct message'
  const names = others.map((id) => users.get(id)?.displayName ?? 'Unknown user')
  if (channel.type === 'dm') return names[0]
  if (names.length <= 3) return names.join(', ')
  return `${names.slice(0, 3).join(', ')} +${names.length - 3}`
}

/** The other side of a one-to-one conversation: the person in a DM, the bot in a bot conversation. */
export function dmPartnerId(channel: LocalChannel, meId: string | null, users?: UserMap): string | null {
  if (channel.type === 'bot_dm') return users ? botIdOf(channel, users, meId) : (otherMemberIds(channel, meId)[0] ?? null)
  if (channel.type !== 'dm') return null
  return otherMemberIds(channel, meId)[0] ?? meId
}

export function isMuted(channel: Pick<LocalChannel, 'mutedUntil'>, now = Date.now()): boolean {
  return Boolean(channel.mutedUntil && new Date(channel.mutedUntil).getTime() > now)
}

export function lastActivity(channel: LocalChannel): string {
  return channel.lastMessage?.createdAt ?? channel.createdAt
}

/**
 * Who may act on someone else's message. The owner role is only carried by channels: the person who started a group
 * chat holds it too, but the server gives it nothing there, so offering them Delete only fails once they have tapped
 * through the confirmation (Permissions.CanDeleteMessage).
 */
export function canModerate(channel: LocalChannel, me: Me | null): boolean {
  return (channel.type === 'channel' && channel.myRole === 'owner') || me?.role === 'admin' || me?.role === 'sysadmin'
}

export function previewText(channel: LocalChannel, users: UserMap, meId: string | null): string {
  const last = channel.lastMessage
  if (!last) return channel.topic || 'No messages yet'
  const who = last.authorId === meId ? 'You' : (users.get(last.authorId)?.displayName.split(' ')[0] ?? 'Someone')
  const body = last.body
    ? last.body.replace(/\s+/g, ' ')
    : last.media
      ? describeLastMedia(last.media)
      : last.hasAttachments
        ? 'Sent an attachment'
        : 'Message deleted'
  const oneToOne = channel.type === 'dm' || channel.type === 'bot_dm'
  return oneToOne && last.authorId !== meId ? body : `${who}: ${body}`
}
